import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    Shareholder,
    ShareholderResidency,
    ShareholderType,
} from '@/api/models';
import type { ShareholderWriteRequest } from '@/api/handlers/resources/dividends';
import {
    useCreateShareholderMutation,
    useLedgerSettingsQuery,
    useShareholdersQuery,
    useDeleteShareholderMutation,
    useUpdateShareholderMutation,
} from '@/api/queries';
import { newEntryId } from '@/pages/accounting/journalEntry/utils';
import {
    bpsToPercentText,
    dividendErrorMessage,
    parsePercentToBps,
} from '../../dividendsMeta';

type UseShareholderFormParams = {
    /** The shareholder being edited; `null` = create. */
    shareholder: Shareholder | null;
    /** The equity date the register was read at — the query key needs it. */
    asOf: string;
    onClose: () => void;
    /** Fired once, after a real save success — never on cancel. */
    onSaved?: () => void;
};

export type ShareholderFormModel = {
    isEdit: boolean;
    nameKa: string;
    nameEn: string;
    identifier: string;
    type: ShareholderType;
    residency: ShareholderResidency;
    holding: string;
    withholdingTax: string;
    withholdingNote: string;
    citExempt: boolean;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    onIdentifierChange: (value: string) => void;
    onTypeChange: (value: ShareholderType) => void;
    onResidencyChange: (value: ShareholderResidency) => void;
    onHoldingChange: (value: string) => void;
    onWithholdingChange: (value: string) => void;
    onWithholdingNoteChange: (value: string) => void;
    onCitExemptChange: (value: boolean) => void;
    /** The entity default, so the form can show what a blank rate falls back to. */
    defaultWithholdingText: string;
    /** True when this holder carries a rate of their own rather than the default. */
    isRateOverridden: boolean;
    submitted: boolean;
    errors: Record<string, string | null>;
    isValid: boolean;
    isSaving: boolean;
    /**
     * Delete is armed by a first press and only runs on the second. Not a
     * dialog: the action already lives inside a drawer, and stacking an overlay
     * on an overlay to ask one question is more ceremony than the decision
     * needs — but it is not a bare button either, because this cannot be undone
     * from the UI.
     */
    isDeleteArmed: boolean;
    onDeleteArm: () => void;
    onDeleteCancel: () => void;
    isDeleting: boolean;
    deleteError: string | null;
    onDelete: () => Promise<void>;
    saveError: string | null;
    onSubmit: () => Promise<void>;
};

/**
 * The add / edit shareholder form.
 *
 * Creating or editing a shareholder produces **no journal entry** — the register
 * is a record of who owns what, not a transaction. Only a declaration posts.
 *
 * The withholding rate is **optional**, and a blank field is the normal case: it
 * stores no rate, and the entity default is then read from ledger settings on the
 * declaration's own date. A rate is entered only for the exceptions — treaty
 * relief, a corporate holder — and once entered it is the accountant's, surviving
 * any later change to the entity default. Seeding the field from the default
 * would turn every holder into a permanent override of a rate nobody chose.
 *
 * Identifier uniqueness is checked against its **own unfiltered fetch** of the
 * register, not against whatever the page happens to be showing — the same lesson
 * the employee form learned.
 */
export const useShareholderForm = ({
    shareholder,
    asOf,
    onClose,
    onSaved,
}: UseShareholderFormParams): ShareholderFormModel => {
    const { t } = useTranslation('pages');
    const isEdit = shareholder !== null;

    const createMutation = useCreateShareholderMutation();
    const updateMutation = useUpdateShareholderMutation();
    const deleteMutation = useDeleteShareholderMutation();
    const registerQuery = useShareholdersQuery(asOf);
    const settingsQuery = useLedgerSettingsQuery();

    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const others = React.useMemo(
        () =>
            registerQuery.data?.success
                ? registerQuery.data.response.items
                : [],
        [registerQuery.data],
    );
    const defaultWithholdingBps = settingsQuery.data?.success
        ? settingsQuery.data.response.settings.rates.dividendWithholdingTaxBps
        : null;

    const [nameKa, setNameKa] = React.useState(shareholder?.name.ka ?? '');
    const [nameEn, setNameEn] = React.useState(shareholder?.name.en ?? '');
    const [identifier, setIdentifier] = React.useState(
        shareholder?.identifier ?? '',
    );
    const [type, setType] = React.useState<ShareholderType>(
        shareholder?.type ?? 'INDIVIDUAL',
    );
    const [residency, setResidency] = React.useState<ShareholderResidency>(
        shareholder?.residency ?? 'RESIDENT',
    );
    const [holding, setHolding] = React.useState(
        shareholder ? bpsToPercentText(shareholder.holdingBps) : '',
    );
    const [withholdingTax, setWithholdingTax] = React.useState(
        shareholder?.withholdingTaxBps == null
            ? ''
            : bpsToPercentText(shareholder.withholdingTaxBps),
    );
    const [withholdingNote, setWithholdingNote] = React.useState(
        shareholder?.withholdingNote ?? '',
    );
    const [citExempt, setCitExempt] = React.useState(
        shareholder?.citExempt ?? false,
    );
    const [submitted, setSubmitted] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [isDeleteArmed, setDeleteArmed] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);
    const [serverIdErrorKey, setServerIdErrorKey] = React.useState<
        string | null
    >(null);

    const onIdentifierChange = React.useCallback((value: string) => {
        setServerIdErrorKey(null);
        setSaveError(null);
        setIdentifier(value);
    }, []);

    const holdingParse = parsePercentToBps(holding);
    const withholdingParse = parsePercentToBps(withholdingTax);

    const errors = React.useMemo<Record<string, string | null>>(() => {
        const percentErrorKey = (
            parsed: ReturnType<typeof parsePercentToBps>,
        ): string | null => {
            if (parsed.ok) {
                return null;
            }
            switch (parsed.reason) {
                case 'empty':
                    return 'dividends.form.errors.required';
                case 'range':
                    return 'dividends.form.errors.percentRange';
                case 'precision':
                    return 'dividends.form.errors.percentPrecision';
                default:
                    return 'dividends.form.errors.percentInvalid';
            }
        };

        const identifierError = (() => {
            const trimmed = identifier.trim();
            if (trimmed === '') {
                return 'dividends.form.errors.identifierRequired';
            }
            if (!/^\d{9}$|^\d{11}$/.test(trimmed)) {
                return 'dividends.form.errors.identifierFormat';
            }
            if (serverIdErrorKey) {
                return serverIdErrorKey;
            }
            if (
                others.some(
                    (holder) =>
                        holder.id !== (shareholder?.id ?? null) &&
                        holder.identifier === trimmed,
                )
            ) {
                return 'dividends.form.errors.identifierTaken';
            }
            return null;
        })();

        return {
            name:
                nameKa.trim() === '' && nameEn.trim() === ''
                    ? 'dividends.form.errors.nameRequired'
                    : null,
            identifier: identifierError,
            holding: percentErrorKey(holdingParse),
            // A blank rate is not a missing one: it means no rate is recorded
            // for this holder and the entity default applies at the point of
            // use, which is the only way to express "whatever the default is".
            withholdingTax:
                withholdingTax.trim() === ''
                    ? null
                    : percentErrorKey(withholdingParse),
        };
    }, [
        nameKa,
        nameEn,
        identifier,
        serverIdErrorKey,
        others,
        shareholder,
        holdingParse,
        withholdingParse,
        withholdingTax,
    ]);

    const isValid = Object.values(errors).every((error) => error === null);

    const buildRequest = React.useCallback(
        (id: string): ShareholderWriteRequest => ({
            id,
            name: {
                ...(nameKa.trim() ? { ka: nameKa.trim() } : {}),
                ...(nameEn.trim() ? { en: nameEn.trim() } : {}),
            },
            identifier: identifier.trim(),
            type,
            residency,
            holdingBps: holdingParse.ok ? holdingParse.bps : 0,
            // Null, not zero: zero is "withhold nothing", which is a rate.
            withholdingTaxBps: withholdingParse.ok
                ? withholdingParse.bps
                : null,
            withholdingNote: withholdingNote.trim(),
            citExempt,
        }),
        [
            nameKa,
            nameEn,
            identifier,
            type,
            residency,
            holdingParse,
            withholdingParse,
            withholdingNote,
            citExempt,
        ],
    );

    const onSubmit = React.useCallback(async () => {
        setSubmitted(true);
        setSaveError(null);
        if (!isValid) {
            return;
        }

        const result = isEdit
            ? await updateMutation.mutateAsync({
                  shareholderId: shareholder.id,
                  body: buildRequest(shareholder.id),
              })
            : await createMutation.mutateAsync(buildRequest(newEntryId()));

        if (!result.success) {
            const code = result.error.code;
            if (
                code === 'IDENTIFIER_INVALID' ||
                code === 'IDENTIFIER_DUPLICATE'
            ) {
                // The identifier is the field's problem, not the form's, so it
                // is shown under the input that can fix it. The two codes get
                // different copy: `DUPLICATE` means the number belongs to
                // someone else, `INVALID` means it is malformed.
                setServerIdErrorKey(
                    code === 'IDENTIFIER_INVALID'
                        ? 'dividends.form.errors.identifierFormat'
                        : 'dividends.form.errors.identifierTaken',
                );
                return;
            }
            setSaveError(dividendErrorMessage(result.error, t));
            return;
        }
        onSaved?.();
        onClose();
    }, [
        isValid,
        isEdit,
        shareholder,
        buildRequest,
        updateMutation,
        createMutation,
        onClose,
        onSaved,
        t,
    ]);

    /**
     * Remove a shareholder outright.
     *
     * Offered without a pre-check, deliberately. Whether a holder is removable
     * depends on whether any declaration ever *paid* them, and the client cannot
     * know that without reading every declaration's breakdown — a second
     * implementation of a rule the server already owns, and one that would go
     * stale the moment a declaration is committed in another tab. So the button
     * is always live and the server is the authority:
     * `SHAREHOLDER_HAS_DECLARATIONS` comes back and its copy points at the
     * alternative.
     */
    const onDelete = React.useCallback(async () => {
        if (!isEdit) {
            return;
        }
        setDeleteError(null);
        const result = await deleteMutation.mutateAsync(shareholder.id);
        if (!result.success) {
            setDeleteError(dividendErrorMessage(result.error, t));
            setDeleteArmed(false);
            return;
        }
        onClose();
    }, [isEdit, shareholder, deleteMutation, onClose, t]);

    return {
        isEdit,
        nameKa,
        nameEn,
        identifier,
        type,
        residency,
        holding,
        withholdingTax,
        withholdingNote,
        citExempt,
        onNameKaChange: setNameKa,
        onNameEnChange: setNameEn,
        onIdentifierChange,
        onTypeChange: setType,
        onResidencyChange: setResidency,
        onHoldingChange: setHolding,
        onWithholdingChange: setWithholdingTax,
        onWithholdingNoteChange: setWithholdingNote,
        onCitExemptChange: setCitExempt,
        defaultWithholdingText:
            defaultWithholdingBps === null
                ? ''
                : bpsToPercentText(defaultWithholdingBps),
        isRateOverridden: withholdingParse.ok,
        submitted,
        errors,
        isValid,
        isSaving: createMutation.isPending || updateMutation.isPending,
        isDeleteArmed,
        onDeleteArm: () => {
            setDeleteError(null);
            setDeleteArmed(true);
        },
        onDeleteCancel: () => setDeleteArmed(false),
        isDeleting: deleteMutation.isPending,
        deleteError,
        onDelete,
        saveError,
        onSubmit,
    };
};
